import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { BarLoader } from 'react-spinners';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { PiDownloadSimple } from 'react-icons/pi';
import { BsFiletypeCsv } from 'react-icons/bs';
import { LiaFileExcel } from 'react-icons/lia';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { RiRefreshLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';
import Pagination from '../pagination/pagination';
import './reports.css';
import {
    DATE_API, PAGE_SIZES, DEFAULT_PAGE_SIZE, selectStyles,
    fmtMoney, fmtDate, DeliveryBadge, ApprovalBadge, toDateObj, TextField, SelectField, exportRows,
} from './purchaseOrderAdvanced';

// Same approval statuses as the GRN report
const STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: '0', label: 'Pending' },
    { value: '1', label: 'Approved' },
    { value: '2', label: 'Hold' },
    { value: '3', label: 'Rejected' },
];

const COLS = [
    { header: 'GRN No', field: 'grnNo', width: 130 },
    { header: 'GRN Date', field: 'grnDate', width: 120, date: true },
    { header: 'PO No', field: 'lpoNo', width: 130 },
    { header: 'Supplier', field: 'supplier', width: 240 },
    { header: 'Supplier Account No', field: 'supplierAccountNo', width: 170 },
    { header: 'Supplier DN', field: 'supplierDN', width: 140 },
    { header: 'DN Date', field: 'dnDate', width: 120, date: true },
    { header: 'Property', field: 'propertyName', width: 180 },
    { header: 'Unit', field: 'unitNo', width: 110 },
    { header: 'Nature of Job', field: 'natureofJob', width: 160 },
    { header: 'Work Order No', field: 'workOrderNo', width: 140 },
    { header: 'Requisition No', field: 'requisitionNo', width: 140 },
    { header: 'Requisition Type', field: 'requisitionType', width: 150 },
    { header: 'Gross', field: 'grossAmount', width: 130, money: true },
    { header: 'Discount', field: 'discountAmount', width: 130, money: true },
    { header: 'Net', field: 'netAmount', width: 130, money: true },
    { header: 'VAT', field: 'vatAmount', width: 120, money: true },
    { header: 'Total', field: 'totalAmount', width: 140, money: true },
    { header: 'Approval Status', field: 'approvalStatus', width: 150, approval: true },
    { header: 'Delivery Status', field: 'deliveryStatus', width: 180, delivery: true },
];
const SUM_FIELDS = ['grossAmount', 'discountAmount', 'netAmount', 'vatAmount', 'totalAmount'];
const EXPORT_CFG = {
    cols: COLS,
    sumFields: SUM_FIELDS,
    title: 'GRN Report Advanced List',
    sheet: 'GRN Report Advanced',
    file: 'GRN_Report_Advanced',
};

const emptyFilters = () => ({
    grnNo: '',
    poNo: '',
    item: null,
    supplierId: null,
    supplier: null,
    stockType: null,
    property: null,
    unit: null,
    status: STATUS_OPTIONS[0],
    fromDate: moment().format(DATE_API),
    toDate: moment().format(DATE_API),
});

// Query-string prefix of the request object in swagger (e.g. 'inventoryGRNReportRequest.'). The PO reports need one;
// if every filter reaches SQL as NULL while PageNo / PageSize arrive, this prefix is what is missing.
const PARAM_PREFIX = 'inventoryGRNReportRequest.';

// Same request parameters as the GRN report. Empty values are omitted (no filter).
const fetchGrnAdvanced = async (apiService, filters, pageNo, pageSize) => {
    const params = new URLSearchParams();
    const add = (key, value) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') params.append(`${PARAM_PREFIX}${key}`, String(value).trim());
    };
    add('GRNNo', filters.grnNo);
    add('Status', filters.status?.value);
    add('ItemNo', filters.item?.value?.itemNo);
    add('ItemDescription', filters.item?.value?.itemDescription);
    add('StockType', filters.stockType?.value);
    add('POrderNo', filters.poNo);
    add('VendorId', filters.supplierId?.label);
    add('VendorName', filters.supplier?.label);
    add('CostCenter1', filters.property?.label);
    add('CostCenter2', filters.unit?.label);
    add('FromDate', filters.fromDate);
    add('ToDate', filters.toDate);
    if (pageSize) {
        params.append('PageNo', pageNo || 1);
        params.append('PageSize', pageSize);
    }
    const res = await apiService.getApiParamater('GET_GRN_ADVANCED_REPORT', `?${params.toString()}`);
    const body = res?.data;
    // 204 = no rows, not an error
    if (!body || body.statusCode === 204) return { rows: [], pagination: null };
    if (body.statusCode && body.statusCode >= 400) throw new Error(body.message || 'Request failed');
    const list = body.result ?? body.data;
    return { rows: Array.isArray(list) ? list : [], pagination: body.paginationData || null };
};

const GrnFilterBar = ({ apiService, stockTypes, suppliers, properties, onApply, onReset }) => {
    const [f, setF] = useState(emptyFilters());
    const [units, setUnits] = useState([]);
    const set = (key) => (value) => setF((p) => ({ ...p, [key]: value }));
    const dateError = f.toDate && !f.fromDate ? 'Pick a From date — a To date on its own is ignored.' : '';
    const toOptions = (list, labelKey) => (list || []).map((x) => ({ value: x, label: x?.[labelKey] }));

    // Units depend on the selected property (same cascade as the other reports)
    const handleProperty = async (opt) => {
        setF((p) => ({ ...p, property: opt, unit: null }));
        setUnits([]);
        if (!opt) return;
        try {
            const res = await apiService.getApiParamater('GET_COST_TWO', `?CostCenter1=${encodeURIComponent(opt.label)}`);
            setUnits(res?.data?.result || []);
        } catch (e) {
            console.error('Error loading units:', e);
        }
    };

    const loadItems = async (input) => {
        try {
            const params = new URLSearchParams();
            params.append('PageNo', 1);
            params.append('PageSize', 20);
            params.append('ItemDescription', input || '');
            const res = await apiService.getApiParamater('GET_ITEM', `?${params.toString()}`);
            return (res?.data?.result || []).map((it) => ({ value: it, label: `${it.itemNo} - ${it.itemDescription}` }));
        } catch (e) {
            console.error('Error loading items:', e);
            return [];
        }
    };

    const apply = () => {
        if (dateError) {
            toast.warning(dateError);
            return;
        }
        onApply(f);
    };
    const reset = () => {
        setF(emptyFilters());
        setUnits([]);
        onReset();
    };

    return (
        <div className="gridBg m-0 mb-3 p-3" style={{ borderRadius: 16 }}>
            <div className="row">
                <TextField label="GRN No" value={f.grnNo} onChange={set('grnNo')} />
                <TextField label="PO No" value={f.poNo} onChange={set('poNo')} />
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">Item</label>
                    <AsyncSelect cacheOptions defaultOptions loadOptions={loadItems} isClearable className="p-0"
                        menuPortalTarget={document.body} styles={selectStyles} value={f.item}
                        placeholder="Item No / Description" onChange={set('item')} />
                </div>
                <SelectField label="Stock Type" value={f.stockType} onChange={set('stockType')} isClearable placeholder="All"
                    options={(stockTypes || []).map((s) => ({ value: s.stockTypeCode, label: s.stockTypeName }))} />
                <SelectField label="Supplier Id" value={f.supplierId} onChange={set('supplierId')} isClearable placeholder="All"
                    options={toOptions(suppliers, 'record_Id')} />
                <SelectField label="Supplier" value={f.supplier} onChange={set('supplier')} isClearable placeholder="All"
                    options={toOptions(suppliers, 'supplierName')} />
                <SelectField label="Property" value={f.property} onChange={handleProperty} isClearable placeholder="All"
                    options={toOptions(properties, 'description')} />
                <SelectField label="Unit" value={f.unit} onChange={set('unit')} isClearable placeholder="All"
                    isDisabled={!f.property} options={toOptions(units, 'description')} />
                <SelectField label="Status" value={f.status} onChange={(o) => set('status')(o || STATUS_OPTIONS[0])} options={STATUS_OPTIONS} />
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">From Date</label>
                    <DatePicker selected={toDateObj(f.fromDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="From"
                        onChange={(d) => set('fromDate')(d ? moment(d).format(DATE_API) : '')} />
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">To Date</label>
                    <DatePicker selected={toDateObj(f.toDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="To" minDate={toDateObj(f.fromDate)}
                        onChange={(d) => set('toDate')(d ? moment(d).format(DATE_API) : '')} />
                    {dateError && <div className="small text-danger">{dateError}</div>}
                </div>
                <div className="col-md-3 col-sm-6 mb-2 d-flex align-items-end gap-2">
                    <div className="mainbtnGry flex-end gap-2 w-50" role="button" tabIndex={0} onClick={apply}
                        onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}>
                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Apply</div>
                    </div>
                    <div className="mainbtnRed flex-end gap-2 w-50" role="button" tabIndex={0} onClick={reset}
                        onKeyDown={(e) => { if (e.key === 'Enter') reset(); }}>
                        <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Reset</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const GrnGrid = ({ rows }) => {
    const colDefs = useMemo(() => COLS.map((c, i) => {
        const def = {
            headerName: c.header,
            field: c.field,
            width: c.width,
            suppressMovable: true,
            filter: true,
            floatingFilter: true,
            ...(i === 0 ? { pinned: 'left' } : {}),
            ...(c.money ? { type: 'rightAligned', headerClass: 'ag-right-aligned-header' } : {}),
        };
        if (c.money) def.valueFormatter = (p) => fmtMoney(p.value);
        else if (c.date) def.valueFormatter = (p) => fmtDate(p.value);
        else if (c.delivery) def.cellRenderer = (p) => <DeliveryBadge value={p.value} />;
        else if (c.approval) def.cellRenderer = (p) => <ApprovalBadge value={p.value} />;
        return def;
    }), []);

    return (
        <div className="ag-theme-quartz" style={{ height: 560 }}>
            <AgGridReact
                rowData={rows}
                columnDefs={colDefs}
                getRowStyle={(p) => ({ background: p.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff' })}
                suppressMovableColumns
            />
        </div>
    );
};

const GrnReportAdvanced = () => {
    const apiService = useRef(new ApiService()).current;
    const reqId = useRef(0);
    const [filters, setFilters] = useState(emptyFilters());
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stockTypes, setStockTypes] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        apiService.getApiParamater('GET_STOCK_TYPE', '?isActive=true')
            .then((res) => setStockTypes(res?.data?.result || []))
            .catch((e) => console.error('Error loading stock types:', e));
        apiService.get('GET_SUPPLIER_KANOO')
            .then((res) => setSuppliers(res?.data?.result || []))
            .catch((e) => console.error('Error loading suppliers:', e));
        apiService.get('GET_COST_ONE')
            .then((res) => setProperties(res?.data?.result || []))
            .catch((e) => console.error('Error loading properties:', e));
    }, [apiService]);

    // Fetches on mount, Apply and page / page-size change only. Stale responses are dropped.
    useEffect(() => {
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        fetchGrnAdvanced(apiService, filters, pageNo, pageSize)
            .then(({ rows: r, pagination: p }) => {
                if (id !== reqId.current) return;
                setRows(r);
                setPagination(p);
            })
            .catch((e) => {
                if (id !== reqId.current) return;
                setRows([]);
                setPagination(null);
                setError(e?.message || 'Unable to load the report.');
            })
            .finally(() => { if (id === reqId.current) setLoading(false); });
    }, [apiService, filters, pageNo, pageSize]);

    const handleApply = (f) => { setFilters({ ...f }); setPageNo(1); };
    const handleReset = () => { setFilters(emptyFilters()); setPageNo(1); };
    // Export covers every matching row: one request with PageNo 1 and PageSize = total records.
    const handleExport = async (type) => {
        const total = pagination?.totalRecords || rows.length;
        if (!total) return;
        setExporting(true);
        try {
            const { rows: all } = await fetchGrnAdvanced(apiService, filters, 1, total);
            exportRows(all, type, EXPORT_CFG);
        } catch (e) {
            toast.error(e?.message || 'Unable to export the report.');
        } finally {
            setExporting(false);
        }
    };
    const handlePageSize = (e) => { setPageSize(Number(e.target.value)); setPageNo(1); };

    return (
        <div className="p-4">
            <GrnFilterBar apiService={apiService} stockTypes={stockTypes} suppliers={suppliers} properties={properties}
                onApply={handleApply} onReset={handleReset} />

            <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                <div className="mainInvHead center">GRN Report Advanced</div>
                <div className="ms-auto">
                    <select className="form-select form-select-sm" aria-label="Page size" value={pageSize} onChange={handlePageSize}>
                        {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / page</option>)}
                    </select>
                </div>
                <div className="mainbtnGry flex-end gap-2">
                    <Dropdown isOpen={exportOpen} toggle={() => setExportOpen(!exportOpen)}>
                        <DropdownToggle tag="span" aria-expanded={exportOpen} role="button" aria-label="Export">
                            <PiDownloadSimple size={25} />
                        </DropdownToggle>
                        <DropdownMenu className="mt-3">
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('csv')} className="gap-3 d-flex center"><BsFiletypeCsv /><div>Export CSV</div></DropdownItem>
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('xlsx')} className="gap-3 d-flex center"><LiaFileExcel /><div>Export Excel</div></DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>

            <div className="gridshad">
                {loading ? (
                    <div className="center p-5"><BarLoader color="#6E5199" height={3} width={200} /></div>
                ) : error ? (
                    <div className="text-danger p-4" role="alert">{error}</div>
                ) : !rows.length ? (
                    <div className="text-muted p-4">No GRN records found.</div>
                ) : (
                    <GrnGrid rows={rows} />
                )}
                {pagination && <Pagination pagination={pagination} getItems={(p) => setPageNo(p)} />}
            </div>
        </div>
    );
};

export default GrnReportAdvanced;
